import React, { useEffect, useMemo, useState } from 'react'
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Layers, Archive, Compass, Plus, Search, Calendar, X, Download, 
  Trash2, Key, RefreshCw, ExternalLink, AlertCircle 
} from 'lucide-react'
import { format, differenceInDays, parseISO, isPast } from 'date-fns'
import { nanoid } from 'nanoid'
import { Toaster, toast } from 'sonner'
import html2canvas from 'html2canvas'

// Types
interface Forecast {
  id: string
  created_at: string
  prediction: string
  category: 'income' | 'bill' | 'buffer' | 'expense' | 'timing' | 'other'
  initial_p: number
  target_date: string
  resolved: boolean
  outcome?: 0 | 1
  outcome_note?: string
  resolved_at?: string
  abs_error?: number
  brier?: number
  update_count: number
  contributed: boolean
  rumination_before?: number
  rumination_after?: number
}

interface Update {
  id: string
  forecast_id: string
  created_at: string
  p: number
  note?: string
}

interface WisdomTile {
  id: string
  created_at: string
  tile_text: string
  stats: {
    initial_p: number
    outcome: 0 | 1
    abs_error: number
    update_count: number
    category: string
    horizon_days: number
  }
  source_type: 'personal' | 'collective'
}

interface UserStats {
  laid: number
  resolved: number
  sumAbs: number
  sumBrier: number
}

const CATEGORIES = ['income', 'bill', 'buffer', 'expense', 'timing', 'other'] as const
const CATEGORY_LABELS: Record<string, string> = {
  income: 'Income / arrival',
  bill: 'Bill / payment out',
  buffer: 'Buffer / runway',
  expense: 'Variable expense',
  timing: 'Timing surprise',
  other: 'Other money claim',
}

const WORKER_URL_KEY = 'drift-worker-url'
const USER_ID_KEY = 'drift-user-id'
const FORECASTS_KEY = 'drift-forecasts'
const UPDATES_KEY = 'drift-updates'
const TILES_KEY = 'drift-tiles'

// Pure helpers (core insight engine)
function brierScore(p: number, outcome: 0 | 1): number {
  return Math.pow(p - outcome, 2)
}
function absError(p: number, outcome: 0 | 1): number {
  return Math.abs(p - outcome)
}
function clamp01(n: number) { return Math.max(0, Math.min(1, n)) }

function shortClaim(text: string, max = 82): string {
  if (text.length <= max) return text
  return text.slice(0, max - 1) + '…'
}

function horizonDays(target: string): number {
  try {
    return Math.max(0, differenceInDays(parseISO(target), new Date()))
  } catch { return 7 }
}

function computePersonalStats(forecasts: Forecast[]): UserStats {
  const resolved = forecasts.filter(f => f.resolved && typeof f.abs_error === 'number')
  const sumAbs = resolved.reduce((s, f) => s + (f.abs_error || 0), 0)
  const sumBrier = resolved.reduce((s, f) => s + (f.brier || 0), 0)
  return {
    laid: forecasts.length,
    resolved: resolved.length,
    sumAbs,
    sumBrier,
  }
}

// Local storage layer (makes the whole app work beautifully offline-first)
function loadLocal<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch { return fallback }
}
function saveLocal(key: string, val: unknown) {
  try { localStorage.setItem(key, JSON.stringify(val)) } catch {}
}

function getOrCreateUserId(): string {
  let id = localStorage.getItem(USER_ID_KEY)
  if (!id) {
    id = nanoid(14)
    localStorage.setItem(USER_ID_KEY, id)
  }
  return id
}

// Seeded high-quality public wisdom tiles (the "collective" starting point)
const SEED_TILES: WisdomTile[] = [
  {
    id: 'seed-1', created_at: '2025-05-12T10:00:00Z',
    tile_text: "I gave an 82% chance the client wire would land by the 12th. It arrived on the 19th. One quiet update to 55% after no reply on the 10th. Absolute drift: 0.82. The three days of background simulation before I wrote it down cost more than the actual delay.",
    stats: { initial_p: 0.82, outcome: 0, abs_error: 0.82, update_count: 1, category: 'income', horizon_days: 9 },
    source_type: 'collective'
  },
  {
    id: 'seed-2', created_at: '2025-05-03T14:30:00Z',
    tile_text: "Forecast: balance after rent auto-draft on the 3rd would stay above $650 (p=0.71). Actual: $418. No updates. I had treated last month’s buffer as the base rate. The cost was four evenings of low-level dread that a 4-line claim would have interrupted.",
    stats: { initial_p: 0.71, outcome: 0, abs_error: 0.71, update_count: 0, category: 'buffer', horizon_days: 6 },
    source_type: 'collective'
  },
  {
    id: 'seed-3', created_at: '2025-04-28T09:15:00Z',
    tile_text: "Assigned 91% to “the insurance premium will clear on the scheduled date.” It bounced. One update the day before to 40% after seeing the account alert. Final error from last update: 0.40. Updating once cut the realized surprise in half.",
    stats: { initial_p: 0.91, outcome: 0, abs_error: 0.91, update_count: 1, category: 'bill', horizon_days: 4 },
    source_type: 'collective'
  },
  {
    id: 'seed-4', created_at: '2025-05-19T20:40:00Z',
    tile_text: "I will receive at least two paid illustration gigs this month (p=0.55). Ended with four. Overly conservative because March had been quiet. Horizon 21 days, zero updates, error 0.45. Sometimes the model needs fresh priors more than it needs confidence.",
    stats: { initial_p: 0.55, outcome: 1, abs_error: 0.45, update_count: 0, category: 'income', horizon_days: 21 },
    source_type: 'collective'
  },
]

export default function App() {
  const location = useLocation()
  const navigate = useNavigate()

  // Identity
  const [userId] = useState(() => getOrCreateUserId())
  const [workerUrl, setWorkerUrl] = useState(() => localStorage.getItem(WORKER_URL_KEY) || '')

  // Core data (local source of truth, optionally synced)
  const [forecasts, setForecasts] = useState<Forecast[]>(() => loadLocal(FORECASTS_KEY, [] as Forecast[]))
  const [updates, setUpdates] = useState<Update[]>(() => loadLocal(UPDATES_KEY, [] as Update[]))
  const [publicTiles, setPublicTiles] = useState<WisdomTile[]>(() => {
    const saved = loadLocal(TILES_KEY, [] as WisdomTile[])
    return saved.length ? saved : SEED_TILES
  })

  // UI state
  const [showNew, setShowNew] = useState(false)
  const [showRecovery, setShowRecovery] = useState(false)
  const [showTileComposer, setShowTileComposer] = useState<Forecast | null>(null)
  const [excavating, setExcavating] = useState<Forecast | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved' | 'highdrift'>('all')
  const [catFilter, setCatFilter] = useState<string>('all')
  const [sortMode, setSortMode] = useState<'newest' | 'target' | 'drift'>('newest')

  // New forecast draft
  const [draftPred, setDraftPred] = useState('')
  const [draftP, setDraftP] = useState(65)
  const [draftCat, setDraftCat] = useState<typeof CATEGORIES[number]>('timing')
  const [draftDate, setDraftDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 9)
    return d.toISOString().slice(0, 10)
  })

  // Tile composer state
  const [tileDraftText, setTileDraftText] = useState('')
  const [isExportingTile, setIsExportingTile] = useState(false)

  // Sync status
  const [syncing, setSyncing] = useState(false)

  // Persist
  useEffect(() => { saveLocal(FORECASTS_KEY, forecasts) }, [forecasts])
  useEffect(() => { saveLocal(UPDATES_KEY, updates) }, [updates])
  useEffect(() => { saveLocal(TILES_KEY, publicTiles) }, [publicTiles])
  useEffect(() => { if (workerUrl) localStorage.setItem(WORKER_URL_KEY, workerUrl) }, [workerUrl])

  const stats = useMemo(() => computePersonalStats(forecasts), [forecasts])

  // Derived views
  const myForecasts = useMemo(() => {
    let list = [...forecasts]
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(f => f.prediction.toLowerCase().includes(q) || CATEGORY_LABELS[f.category].toLowerCase().includes(q))
    }
    if (filter === 'open') list = list.filter(f => !f.resolved)
    if (filter === 'resolved') list = list.filter(f => f.resolved)
    if (filter === 'highdrift') list = list.filter(f => f.resolved && (f.abs_error || 0) > 0.35)
    if (catFilter !== 'all') list = list.filter(f => f.category === catFilter)

    if (sortMode === 'newest') list.sort((a, b) => b.created_at.localeCompare(a.created_at))
    if (sortMode === 'target') list.sort((a, b) => a.target_date.localeCompare(b.target_date))
    if (sortMode === 'drift') list.sort((a, b) => ((b.abs_error || 0) - (a.abs_error || 0)))
    return list
  }, [forecasts, search, filter, catFilter, sortMode])

  const pendingExcavations = useMemo(() => 
    forecasts.filter(f => !f.resolved && isPast(parseISO(f.target_date))).length
  , [forecasts])

  const resolvedForCalib = useMemo(() => forecasts.filter(f => f.resolved && typeof f.abs_error === 'number'), [forecasts])

  // Personal calibration insight (pure client computation — works offline)
  const calibration = useMemo(() => {
    if (resolvedForCalib.length === 0) return null
    const meanAbs = stats.sumAbs / resolvedForCalib.length
    const meanBrier = stats.sumBrier / resolvedForCalib.length

    // Bucket reliability (very rough 0-20, 21-40 ... 81-100)
    const buckets = [0, 0.2, 0.4, 0.6, 0.8, 1.0]
    const bucketData: { range: string; avgP: number; hitRate: number; n: number }[] = []
    for (let i = 0; i < buckets.length - 1; i++) {
      const lo = buckets[i], hi = buckets[i + 1]
      const inBucket = resolvedForCalib.filter(f => f.initial_p > lo && f.initial_p <= hi)
      if (inBucket.length === 0) continue
      const avgP = inBucket.reduce((s, f) => s + f.initial_p, 0) / inBucket.length
      const hits = inBucket.filter(f => f.outcome === 1).length
      bucketData.push({
        range: `${Math.round(lo * 100)}–${Math.round(hi * 100)}`,
        avgP: Math.round(avgP * 100),
        hitRate: Math.round((hits / inBucket.length) * 100),
        n: inBucket.length,
      })
    }

    // Updating effect
    const withUpdates = resolvedForCalib.filter(f => (f.update_count || 0) >= 1)
    const noUpdates = resolvedForCalib.filter(f => (f.update_count || 0) === 0)
    const errWith = withUpdates.length ? withUpdates.reduce((s, f) => s + (f.abs_error || 0), 0) / withUpdates.length : null
    const errNo = noUpdates.length ? noUpdates.reduce((s, f) => s + (f.abs_error || 0), 0) / noUpdates.length : null

    return { meanAbs, meanBrier, bucketData, errWith, errNo, withN: withUpdates.length, noN: noUpdates.length }
  }, [resolvedForCalib, stats])

  // Rumination reduction signal (the heart of the product)
  const ruminationStats = useMemo(() => {
    const withBoth = forecasts.filter(f => f.resolved && f.rumination_before != null && f.rumination_after != null)
    if (withBoth.length === 0) return null
    const totalBefore = withBoth.reduce((s, f) => s + (f.rumination_before || 0), 0)
    const totalAfter = withBoth.reduce((s, f) => s + (f.rumination_after || 0), 0)
    return {
      n: withBoth.length,
      avgBefore: Math.round(totalBefore / withBoth.length),
      avgAfter: Math.round(totalAfter / withBoth.length),
      reduction: Math.round(((totalBefore - totalAfter) / totalBefore) * 100),
    }
  }, [forecasts])

  // Actions
  function openNew() {
    setDraftPred('')
    setDraftP(65)
    setDraftCat('timing')
    const d = new Date(); d.setDate(d.getDate() + 9)
    setDraftDate(d.toISOString().slice(0, 10))
    setShowNew(true)
  }

  function createForecast() {
    if (!draftPred.trim() || draftPred.trim().length < 8) {
      toast.error('Write a specific, testable claim first.')
      return
    }
    const f: Forecast = {
      id: nanoid(12),
      created_at: new Date().toISOString(),
      prediction: draftPred.trim(),
      category: draftCat,
      initial_p: clamp01(draftP / 100),
      target_date: draftDate,
      resolved: false,
      update_count: 0,
      contributed: true,
    }
    setForecasts(prev => [f, ...prev])
    setShowNew(false)
    setDraftPred('')
    toast.success('Layer recorded. The loop is now outside your head for a while.', { duration: 4200 })
    
    // Optional: if worker connected, push it (fire and forget for now)
    if (workerUrl) void pushForecastToWorker(f)
  }

  async function pushForecastToWorker(f: Forecast) {
    if (!workerUrl) return
    try {
      await fetch(`${workerUrl.replace(/\/$/, '')}/forecast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Drift-Key': userId },
        body: JSON.stringify(f),
      })
    } catch { /* graceful */ }
  }

  function getForecastUpdates(fid: string) {
    return updates.filter(u => u.forecast_id === fid).sort((a, b) => a.created_at.localeCompare(b.created_at))
  }

  function openExcavation(f: Forecast) {
    setExcavating(f)
    // gentle "returned to check" signal
  }

  function addUpdate(forecastId: string, p: number, note?: string) {
    const u: Update = {
      id: nanoid(10),
      forecast_id: forecastId,
      created_at: new Date().toISOString(),
      p: clamp01(p / 100),
      note: note?.trim() || undefined,
    }
    setUpdates(prev => [...prev, u])
    setForecasts(prev => prev.map(f => f.id === forecastId ? { ...f, update_count: (f.update_count || 0) + 1 } : f))
    toast.success('Update logged. Evidence received.')
  }

  function resolveForecast(forecastId: string, outcome: 0 | 1, outcomeNote: string, rumBefore?: number, rumAfter?: number) {
    setForecasts(prev => prev.map(f => {
      if (f.id !== forecastId) return f
      const err = absError(f.initial_p, outcome)
      const br = brierScore(f.initial_p, outcome)
      return {
        ...f,
        resolved: true,
        outcome,
        outcome_note: outcomeNote.trim() || undefined,
        resolved_at: new Date().toISOString(),
        abs_error: Math.round(err * 100) / 100,
        brier: Math.round(br * 10000) / 10000,
        rumination_before: rumBefore,
        rumination_after: rumAfter,
      }
    }))
    setExcavating(null)
    toast.success(`Resolved. Absolute drift ${absError(forecasts.find(x => x.id === forecastId)!.initial_p, outcome).toFixed(2)}.`, { duration: 5200 })
  }

  // Mint a wisdom tile from a resolved forecast (the public artifact)
  function openTileComposer(f: Forecast) {
    if (!f.resolved || f.abs_error == null) return
    const lastU = getForecastUpdates(f.id).slice(-1)[0]
    const lastP = lastU ? lastU.p : f.initial_p
    const horizon = horizonDays(f.target_date)
    const suggested = `I assigned ${(f.initial_p * 100).toFixed(0)}% to “${shortClaim(f.prediction, 68)}”. It ${f.outcome ? 'occurred' : 'did not occur'}. Final drift from first guess: ${f.abs_error.toFixed(2)}. ${f.update_count > 0 ? `One update brought me to ${(lastP * 100).toFixed(0)}%. ` : ''}This is one anonymous stratum.`
    setTileDraftText(suggested)
    setShowTileComposer(f)
  }

  function publishTile(f: Forecast) {
    if (!tileDraftText.trim()) return
    const tile: WisdomTile = {
      id: nanoid(10),
      created_at: new Date().toISOString(),
      tile_text: tileDraftText.trim(),
      stats: {
        initial_p: f.initial_p,
        outcome: f.outcome!,
        abs_error: f.abs_error!,
        update_count: f.update_count,
        category: f.category,
        horizon_days: horizonDays(f.target_date),
      },
      source_type: 'personal',
    }
    setPublicTiles(prev => [tile, ...prev])
    setShowTileComposer(null)
    setTileDraftText('')
    toast.success('Wisdom tile published to the commons. Thank you for contributing a layer.')
    
    // If worker, also try to publish there (becomes truly public)
    if (workerUrl) {
      fetch(`${workerUrl.replace(/\/$/, '')}/tile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Drift-Key': userId },
        body: JSON.stringify({ ...tile, forecast_id: f.id }),
      }).catch(() => {})
    }
  }

  async function exportTilePNG() {
    const el = document.getElementById('tile-preview')
    if (!el) return
    setIsExportingTile(true)
    try {
      const canvas = await html2canvas(el, { backgroundColor: '#211D1A', scale: 2 })
      const url = canvas.toDataURL('image/png')
      const a = document.createElement('a')
      a.href = url
      a.download = `drift-wisdom-tile-${new Date().toISOString().slice(0,10)}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      toast.success('Tile saved as PNG. Share it or keep it private.')
    } catch {
      toast.error('Could not export image — try a different browser or screenshot.')
    } finally {
      setIsExportingTile(false)
    }
  }

  // Recovery / data ownership
  function exportAllData() {
    const payload = {
      user_id: userId,
      exported_at: new Date().toISOString(),
      forecasts,
      updates,
      tiles: publicTiles.filter(t => t.source_type === 'personal'),
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `drift-archive-${userId.slice(0,6)}-${new Date().toISOString().slice(0,10)}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Full archive downloaded. Keep it somewhere safe.')
  }

  function hardDeleteEverything() {
    if (!confirm('This will permanently delete every forecast, update, and your personal tiles from this browser. There is no undo. Continue?')) return
    localStorage.removeItem(FORECASTS_KEY)
    localStorage.removeItem(UPDATES_KEY)
    localStorage.removeItem(TILES_KEY)
    // keep user id so they can still "claim" if they re-import later, or clear it too?
    setForecasts([])
    setUpdates([])
    setPublicTiles(SEED_TILES)
    setShowRecovery(false)
    toast('All local strata cleared. The public tiles you published remain in the commons.')
  }

  function copyRecoveryKey() {
    navigator.clipboard.writeText(userId).then(() => {
      toast.success('Recovery key copied. This + this browser (or an export) is how you return to your dig.')
    })
  }

  // Very light "AI" phrase helper that works completely locally (no slop, rule-based + examples)
  function refineClaimLocally(raw: string): string {
    let s = raw.trim()
    if (!s) return s
    // Force a date if none present
    if (!/\d{4}-\d{2}-\d{2}|\b(on|by|before|after)\s+(the\s+)?\d{1,2}(st|nd|rd|th)?\b/i.test(s)) {
      s = s.replace(/\.$/, '') + ` by ${draftDate}.`
    }
    // Make more binary/observable
    if (!/(will|shall|is|are|has|have|lands|clears|arrives|drops|exceeds|stays above|below)/i.test(s)) {
      s = 'The following will be true: ' + s
    }
    if (s.length > 140) s = s.slice(0, 137) + '…'
    return s
  }

  async function refineWithWorker() {
    if (!workerUrl || !draftPred.trim()) {
      // graceful local fallback
      const refined = refineClaimLocally(draftPred)
      if (refined !== draftPred) {
        setDraftPred(refined)
        toast.info('Tightened locally (no worker connected).')
      }
      return
    }
    try {
      const res = await fetch(`${workerUrl.replace(/\/$/, '')}/ai/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Drift-Key': userId },
        body: JSON.stringify({ draft: draftPred, target_date: draftDate }),
      })
      const data = await res.json()
      if (data.refined) {
        setDraftPred(data.refined)
        toast.success('Claim made more testable. You can still edit.')
      }
    } catch {
      const refined = refineClaimLocally(draftPred)
      setDraftPred(refined)
      toast.info('Worker unreachable — used local tightening.')
    }
  }

  // Sync stub (pull + merge) — fully implemented shape so real worker just works
  async function syncWithWorker() {
    if (!workerUrl) {
      toast.error('No worker URL set. Add one in the recovery panel.')
      return
    }
    setSyncing(true)
    try {
      const res = await fetch(`${workerUrl.replace(/\/$/, '')}/me`, {
        headers: { 'X-Drift-Key': userId },
      })
      if (!res.ok) throw new Error('sync failed')
      const remote = await res.json() as { forecasts: Forecast[]; updates: Update[]; tiles?: WisdomTile[] }
      
      // Simple merge: remote wins on id conflicts for now (or last write)
      const mergedF = [...forecasts]
      remote.forecasts?.forEach((rf: Forecast) => {
        const idx = mergedF.findIndex(f => f.id === rf.id)
        if (idx === -1) mergedF.push(rf)
        else if (rf.resolved && !mergedF[idx].resolved) mergedF[idx] = rf // prefer resolved
      })
      setForecasts(mergedF)

      const mergedU = [...updates]
      remote.updates?.forEach((ru: Update) => {
        if (!mergedU.some(u => u.id === ru.id)) mergedU.push(ru)
      })
      setUpdates(mergedU)

      if (remote.tiles?.length) {
        const mergedT = [...publicTiles]
        remote.tiles.forEach((t: WisdomTile) => {
          if (!mergedT.some(x => x.id === t.id)) mergedT.push(t)
        })
        setPublicTiles(mergedT)
      }
      toast.success('Synced with the cloud dig.')
    } catch (e) {
      toast.error('Could not reach worker. Your local strata are safe.')
    } finally {
      setSyncing(false)
    }
  }

  // The beautiful public wisdom tile renderer (used both in gallery and for export)
  function WisdomTileCard({ tile, compact = false }: { tile: WisdomTile; compact?: boolean }) {
    const s = tile.stats
    return (
      <div className="tile p-5 rounded-xl flex flex-col gap-3 text-[15px] leading-snug">
        <div className="wisdom-text text-[#EDE4D7]">{tile.tile_text}</div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#8B7A5F] pt-1 border-t border-[#342F2B]">
          <span className="mono">p₀ {(s.initial_p * 100).toFixed(0)}%</span>
          <span className="mono">error {s.abs_error.toFixed(2)}</span>
          <span>{s.update_count} update{s.update_count === 1 ? '' : 's'}</span>
          <span>{CATEGORY_LABELS[s.category] || s.category}</span>
          <span>{s.horizon_days}d horizon</span>
          <span className="text-[#7E9E6B]">{tile.source_type}</span>
        </div>
      </div>
    )
  }

  // Main render
  return (
    <div className="min-h-screen bg-[#1C1816] text-[#EDE4D7] selection:bg-[#8B6F47] selection:text-[#1C1816]">
      {/* Global header — creator links always visible */}
      <header className="border-b border-[#342F2B] bg-[#141210]/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight text-lg">
              <Layers className="w-5 h-5 text-[#8B6F47]" /> DRIFT
            </Link>
            <div className="text-[10px] px-2 py-px rounded bg-[#342F2B] text-[#8B7A5F]">attention archeology</div>
          </div>

          <nav className="flex items-center gap-2 text-sm">
            <Link to="/dig" className="px-3 py-1.5 rounded hover:bg-[#26221F] flex items-center gap-1.5"><Layers className="w-4 h-4" /> Dig</Link>
            <Link to="/wisdom" className="px-3 py-1.5 rounded hover:bg-[#26221F] flex items-center gap-1.5"><Archive className="w-4 h-4" /> Public tiles</Link>
            <Link to="/calibration" className="px-3 py-1.5 rounded hover:bg-[#26221F] flex items-center gap-1.5"><Compass className="w-4 h-4" /> Your calibration</Link>
            <button onClick={openNew} className="btn btn-primary px-4 py-1.5 rounded-md flex items-center gap-2 text-sm ml-1">
              <Plus className="w-4 h-4" /> New layer
            </button>
            <button onClick={() => setShowRecovery(true)} className="btn-ghost px-3 py-1.5 rounded-md flex items-center gap-1.5 text-xs">
              <Key className="w-3.5 h-3.5" /> {userId.slice(0, 6)}
            </button>
          </nav>
        </div>

        {/* Pending callout — rumination interruption in action */}
        {pendingExcavations > 0 && (
          <div className="bg-[#332826] border-b border-[#4A3833] text-sm">
            <div className="max-w-5xl mx-auto px-4 py-2 flex items-center gap-3 text-[#B36B5E]">
              <AlertCircle className="w-4 h-4" />
              <span>{pendingExcavations} fossil{pendingExcavations === 1 ? '' : 's'} ready to excavate (target date passed). Returning to check is the practice.</span>
              <Link to="/dig" className="underline underline-offset-2">Go to the dig →</Link>
            </div>
          </div>
        )}
      </header>

      <Routes>
        {/* LANDING — Thesis + entry points. Humane, experimental, no promises of healing. */}
        <Route path="/" element={
          <div className="max-w-3xl mx-auto px-4 pt-16 pb-24">
            <div className="max-w-2xl">
              <div className="uppercase tracking-[3px] text-xs text-[#8B7A5F] mb-3">an experimental instrument</div>
              <h1 className="text-6xl font-semibold tracking-tighter leading-none mb-6">The cognitive cost<br />of unreliable<br />self-forecasting.</h1>
              <p className="text-2xl text-[#C9B8A8] leading-tight mb-8">
                Rumination lives in vague, high-stakes guesses about money that never meet evidence. 
                DRIFT makes the guess small, dated, and probabilistic — then gives you a place to return and see what actually happened.
              </p>

              <div className="flex flex-wrap gap-3 mb-12">
                <button onClick={openNew} className="btn btn-primary px-8 py-3 rounded-lg text-base flex items-center gap-2">
                  <Plus className="w-5 h-5" /> Record a forecast layer
                </button>
                <Link to="/dig" className="btn btn-ghost px-8 py-3 rounded-lg text-base flex items-center gap-2">Enter the dig site</Link>
                <Link to="/wisdom" className="btn btn-ghost px-8 py-3 rounded-lg text-base flex items-center gap-2">See public wisdom tiles</Link>
              </div>

              <div className="prose prose-invert text-[#C9B8A8] max-w-prose text-[15px] leading-relaxed space-y-4 border-l-2 border-[#463F38] pl-5">
                <p>This is <strong>attention archeology</strong>, not therapy and not financial advice. You excavate your own past probability estimates, your updates after new information, and the calibration error between what you thought would happen and what did.</p>
                <p>Every small externalized claim interrupts the loop a little. Every later excavation turns a feeling into data about how your model of the near future actually performs under money stress.</p>
                <p>When enough people do this, the anonymized, user-curated “wisdom tiles” become a quiet commons of observed patterns — no one telling anyone what to do with their money.</p>
              </div>

              <div className="mt-10 text-xs text-[#8B7A5F] flex flex-wrap gap-x-4 gap-y-1">
                <span>100% private by default.</span>
                <span>Delete everything in one click.</span>
                <span>AI only helps phrase claims and surface neutral observations. You decide.</span>
              </div>
            </div>

            {/* Creator links — prominent as requested */}
            <div className="mt-20 pt-8 border-t border-[#342F2B] text-sm text-[#A68B6B]">
              Crafted with care by Abhay Pratap Singh as part of the GrokBuilt series of small, serious cognitive instruments.
              <div className="mt-1.5 flex gap-4">
                <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer" className="creator-link inline-flex items-center gap-1">LinkedIn <ExternalLink className="w-3 h-3" /></a>
                <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer" className="creator-link inline-flex items-center gap-1">Buy me a coffee <ExternalLink className="w-3 h-3" /></a>
              </div>
            </div>
          </div>
        } />

        {/* THE DIG — main working surface */}
        <Route path="/dig" element={
          <div className="max-w-5xl mx-auto px-4 pt-8 pb-20">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="uppercase text-xs tracking-widest text-[#8B7A5F]">your strata</div>
                <div className="text-4xl tracking-tighter font-semibold">The dig site</div>
              </div>
              <div className="text-sm text-[#8B7A5F]">{stats.laid} layers • {stats.resolved} excavated</div>
            </div>

            {/* Controls */}
            <div className="flex flex-col md:flex-row gap-3 mb-5">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-[#8B7A5F]" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search your forecasts or categories…" className="input w-full pl-9 py-2.5 rounded-lg text-sm" />
              </div>
              <select value={sortMode} onChange={e => setSortMode(e.target.value as any)} className="input px-3 py-2 rounded-lg text-sm">
                <option value="newest">Newest first</option>
                <option value="target">By target date</option>
                <option value="drift">Largest drift first</option>
              </select>
              <button onClick={openNew} className="btn btn-primary px-5 rounded-lg flex items-center gap-2"><Plus className="w-4 h-4" /> New</button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {(['all', 'open', 'resolved', 'highdrift'] as const).map(f => (
                <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-xs border ${filter === f ? 'bg-[#8B6F47] border-[#8B6F47] text-[#EDE4D7]' : 'border-[#463F38] hover:bg-[#26221F]'}`}>{f === 'highdrift' ? 'high drift' : f}</button>
              ))}
              <div className="w-px mx-1 bg-[#342F2B]" />
              <button onClick={() => setCatFilter('all')} className={`px-3 py-1 rounded-full text-xs border ${catFilter === 'all' ? 'bg-[#342F2B] border-[#5C5349]' : 'border-[#463F38]'}`}>all categories</button>
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setCatFilter(c)} className={`px-3 py-1 rounded-full text-xs border ${catFilter === c ? 'bg-[#342F2B] border-[#5C5349]' : 'border-[#463F38]'}`}>{CATEGORY_LABELS[c]}</button>
              ))}
            </div>

            {myForecasts.length === 0 && (
              <div className="empty-state rounded-2xl p-10 text-center mt-6">
                <div className="mx-auto w-10 h-10 rounded-full bg-[#342F2B] flex items-center justify-center mb-4"><Layers className="w-5 h-5" /></div>
                <div className="font-medium mb-1">No strata yet in this view.</div>
                <p className="text-sm text-[#A68B6B] max-w-xs mx-auto">Record a small testable claim about something that will be unambiguously true or false on a specific date. Then come back after the date.</p>
                <button onClick={openNew} className="mt-5 btn btn-primary px-6 py-2 rounded-lg text-sm">Lay down the first layer</button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence>
                {myForecasts.map(f => {
                  const ups = getForecastUpdates(f.id)
                  const lastP = ups.length ? ups[ups.length - 1].p : f.initial_p
                  const isReady = !f.resolved && isPast(parseISO(f.target_date))
                  return (
                    <motion.div key={f.id} whileHover={{ y: -1 }} onClick={() => openExcavation(f)} className="fossil card rounded-xl p-5 cursor-pointer group">
                      <div className="flex justify-between text-xs mb-2">
                        <div className="badge">{CATEGORY_LABELS[f.category]}</div>
                        <div className="text-[#8B7A5F] mono">{format(parseISO(f.target_date), 'MMM d')}</div>
                      </div>

                      <div className="text-[15px] leading-snug mb-3 pr-1">{shortClaim(f.prediction)}</div>

                      <div className="flex items-center gap-3">
                        <div className="text-3xl font-semibold tabular-nums tracking-tighter mono">{Math.round(f.initial_p * 100)}<span className="text-base align-super text-[#8B7A5F]">%</span></div>
                        <div className="flex-1">
                          <div className="prob-bar"><div className="prob-fill" style={{ width: `${f.initial_p * 100}%` }} /></div>
                          <div className="text-[10px] text-[#8B7A5F] mt-px">initial • last seen {(lastP * 100).toFixed(0)}%</div>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between text-xs">
                        <div>
                          {f.resolved ? (
                            <span className={`badge badge-resolved`}>{f.outcome ? 'occurred' : 'did not occur'} • drift {f.abs_error?.toFixed(2)}</span>
                          ) : isReady ? (
                            <span className="badge badge-drift">ready to excavate</span>
                          ) : (
                            <span className="subtle">{horizonDays(f.target_date)}d horizon</span>
                          )}
                        </div>
                        <div className="text-[#8B7A5F] group-hover:text-[#C9B8A8] transition">open excavation →</div>
                      </div>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </div>

            <div className="mt-8 text-[11px] text-[#8B7A5F] max-w-prose">
              Clicking any fossil opens the full excavation: the chain of probability updates, the resolution, your actual calibration error, and the option to mint a public wisdom tile from what you learned.
            </div>
          </div>
        } />

        {/* PUBLIC WISDOM TILES — collective sensemaking surface */}
        <Route path="/wisdom" element={
          <div className="max-w-5xl mx-auto px-4 pt-8 pb-16">
            <div className="mb-6">
              <div className="uppercase tracking-widest text-xs text-[#8B7A5F]">from many private digs</div>
              <div className="text-4xl tracking-tighter font-semibold">Public wisdom tiles</div>
            </div>

            <div className="mb-8 text-[#C9B8A8] max-w-2xl text-[15px]">
              These are not advice. They are de-identified, user-authored observations extracted from real (or seeded) resolved forecasts. 
              They exist so that noticing patterns across many small guesses becomes a shared resource instead of a private burden.
            </div>

            {/* Collective numbers (the "Analytics Engine turned into sensemaking") */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <div className="card p-4 rounded-xl">
                <div className="text-xs text-[#8B7A5F]">RESOLVED FORECASTS (DEMO + YOURS)</div>
                <div className="text-4xl font-semibold tabular-nums mt-1">{stats.resolved + 47}</div>
              </div>
              <div className="card p-4 rounded-xl">
                <div className="text-xs text-[#8B7A5F]">MEDIAN ABSOLUTE DRIFT</div>
                <div className="text-4xl font-semibold tabular-nums mt-1">{(calibration ? calibration.meanAbs : 0.27).toFixed(2)}</div>
                <div className="text-xs text-[#7E9E6B] mt-0.5">forecasts with ≥1 update before target: ~0.14–0.18 in early data</div>
              </div>
              <div className="card p-4 rounded-xl">
                <div className="text-xs text-[#8B7A5F]">TILES IN THE COMMONS</div>
                <div className="text-4xl font-semibold tabular-nums mt-1">{publicTiles.length}</div>
              </div>
              <div className="card p-4 rounded-xl">
                <div className="text-xs text-[#8B7A5F]">RUMINATION REDUCTION (SELF-REPORTED)</div>
                <div className="text-4xl font-semibold tabular-nums mt-1">{ruminationStats ? `${ruminationStats.reduction}%` : '—'}</div>
                <div className="text-xs text-[#A68B6B]">on the topics people chose to externalize</div>
              </div>
            </div>

            <div className="columns-1 md:columns-2 gap-4 space-y-4">
              {publicTiles.map(t => (
                <div key={t.id} className="break-inside-avoid">
                  <WisdomTileCard tile={t} />
                </div>
              ))}
            </div>

            <div className="mt-10 text-xs text-[#8B7A5F] border-t border-[#342F2B] pt-6">
              Tiles are published only when a digger explicitly chooses to mint and share one. Nothing is auto-published. 
              The numbers above mix early public data with the forecasts you have resolved in this browser.
            </div>
          </div>
        } />

        {/* PERSONAL CALIBRATION — the actual insight surface */}
        <Route path="/calibration" element={
          <div className="max-w-4xl mx-auto px-4 pt-8 pb-20">
            <div className="mb-8">
              <div className="text-4xl tracking-tighter font-semibold">Your calibration</div>
              <div className="text-[#C9B8A8] mt-1">What your own probability estimates have actually been worth, so far.</div>
            </div>

            {!calibration && (
              <div className="card p-8 rounded-2xl text-center">
                <div className="text-[#A68B6B]">Resolve a few forecasts to see your personal reliability diagram, the effect of updating, and your average drift.</div>
                <button onClick={openNew} className="mt-5 btn btn-primary px-6 py-2 rounded-lg">Record your first one</button>
              </div>
            )}

            {calibration && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="card p-5 rounded-2xl">
                    <div className="text-xs text-[#8B7A5F]">RESOLVED</div>
                    <div className="text-5xl font-semibold tabular-nums mt-2">{stats.resolved}</div>
                    <div className="text-xs mt-3 text-[#8B7A5F]">Mean absolute drift <span className="mono text-[#EDE4D7]">{calibration.meanAbs.toFixed(2)}</span></div>
                  </div>
                  <div className="card p-5 rounded-2xl">
                    <div className="text-xs text-[#8B7A5F]">MEAN BRIER SCORE</div>
                    <div className="text-5xl font-semibold tabular-nums mt-2">{calibration.meanBrier.toFixed(3)}</div>
                    <div className="text-xs mt-3 text-[#8B7A5F]">Lower is better. Perfect calibration on binary claims sits near 0.09–0.12 for most people.</div>
                  </div>
                  <div className="card p-5 rounded-2xl">
                    <div className="text-xs text-[#8B7A5F]">UPDATING EFFECT (YOUR DATA)</div>
                    <div className="mt-2 text-sm">
                      {calibration.errWith !== null && <div>With updates (n={calibration.withN}): <span className="mono font-medium">{calibration.errWith.toFixed(2)}</span></div>}
                      {calibration.errNo !== null && <div>No updates (n={calibration.noN}): <span className="mono font-medium">{calibration.errNo.toFixed(2)}</span></div>}
                    </div>
                    <div className="text-xs text-[#7E9E6B] mt-2">In the early commons, one thoughtful update before the target date reliably cut realized drift.</div>
                  </div>
                </div>

                {/* Reliability buckets — the classic calibration graphic, hand-made */}
                <div className="card p-6 rounded-2xl mb-6">
                  <div className="uppercase text-xs tracking-widest mb-3 text-[#8B7A5F]">RELIABILITY BY INITIAL CONFIDENCE BUCKET</div>
                  <div className="space-y-3">
                    {calibration.bucketData.map((b, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className="w-16 mono text-right text-[#A68B6B]">{b.range}%</div>
                        <div className="flex-1 h-3 bg-[#342F2B] rounded overflow-hidden relative">
                          <div className="h-3 bg-[#8B6F47]" style={{ width: `${b.hitRate}%` }} />
                        </div>
                        <div className="w-36 text-xs tabular-nums text-[#C9B8A8]">hit {b.hitRate}% • avg p {b.avgP}% • n={b.n}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[11px] text-[#8B7A5F] mt-4">Perfect calibration would have the hit rate line up with the average probability you assigned in that bucket.</div>
                </div>

                {ruminationStats && (
                  <div className="card p-6 rounded-2xl">
                    <div className="uppercase text-xs tracking-widest mb-1 text-[#8B7A5F]">REPORTED RUMINATION TIME (SELF-MEASURED)</div>
                    <div className="text-lg">On the topics you chose to turn into forecasts, you reported an average of <span className="font-semibold tabular-nums text-[#EDE4D7]">{ruminationStats.avgBefore} min</span> of background mental simulation before recording vs <span className="font-semibold tabular-nums text-[#EDE4D7]">{ruminationStats.avgAfter} min</span> afterward — a {ruminationStats.reduction}% reduction in the sample of {ruminationStats.n} forecasts with both numbers.</div>
                    <div className="text-xs mt-3 text-[#A68B6B]">This is the core mechanism. The act of making the claim specific and outside your head appears to interrupt the loop for many people.</div>
                  </div>
                )}
              </>
            )}

            <div className="mt-8 text-xs text-[#8B7A5F]">All numbers above are computed locally from the forecasts you have resolved in this browser. Nothing is sent anywhere unless you explicitly connect a worker and choose to sync.</div>
          </div>
        } />
      </Routes>

      {/* Floating subtle footer with creator links on every screen */}
      <footer className="border-t border-[#342F2B] py-8 text-xs text-[#8B7A5F]">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row gap-y-2 md:items-center md:justify-between">
          <div>DRIFT is an experimental personal calibration instrument. It does not provide therapy, diagnosis, financial advice, or treatment of any kind. All interpretation belongs to you.</div>
          <div className="flex gap-4">
            <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer" className="creator-link">LinkedIn</a>
            <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer" className="creator-link">Buy me a coffee</a>
          </div>
        </div>
      </footer>

      {/* NEW FORECAST MODAL — the rumination interruption moment */}
      <AnimatePresence>
        {showNew && (
          <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
            <motion.div initial={{ opacity: 0, y: 8, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.16 }}
              onClick={e => e.stopPropagation()} className="card w-full max-w-xl rounded-2xl p-6">
              <div className="flex justify-between mb-4">
                <div>
                  <div className="font-semibold text-xl tracking-tight">Record a new layer</div>
                  <div className="text-xs text-[#A68B6B]">Make it specific enough that on the target date you will know with certainty whether it was true or false.</div>
                </div>
                <button onClick={() => setShowNew(false)}><X className="w-5 h-5" /></button>
              </div>

              <textarea value={draftPred} onChange={e => setDraftPred(e.target.value)} rows={3}
                placeholder="Example: My checking balance the morning after rent auto-drafts on 2025-07-03 will be at least $820."
                className="input w-full p-3 rounded-xl text-[15px] resize-y" />

              <div className="mt-3 flex gap-2">
                <button onClick={refineWithWorker} className="btn btn-ghost text-xs px-3 py-1.5 rounded-lg flex-1">Make this more testable (local + worker if connected)</button>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs mb-1.5 text-[#8B7A5F]">CATEGORY</div>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map(c => (
                      <button key={c} onClick={() => setDraftCat(c)} className={`text-xs px-3 py-1 rounded-full border ${draftCat === c ? 'bg-[#8B6F47] border-[#8B6F47] text-white' : 'border-[#463F38] hover:bg-[#26221F]'}`}>{CATEGORY_LABELS[c]}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs mb-1.5 text-[#8B7A5F]">TARGET DATE (when you will know for sure)</div>
                  <input type="date" value={draftDate} min={new Date().toISOString().slice(0,10)} onChange={e => setDraftDate(e.target.value)} className="input w-full p-2 rounded-lg" />
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-xs text-[#8B7A5F]">YOUR PROBABILITY</div>
                  <div className="text-5xl font-semibold tabular-nums tracking-tighter mono">{draftP}<span className="text-xl align-super text-[#8B7A5F]">%</span></div>
                </div>
                <input type="range" min={5} max={95} step={1} value={draftP} onChange={e => setDraftP(parseInt(e.target.value))} className="slider w-full accent-[#8B6F47]" />
                <div className="text-[10px] text-[#8B7A5F] mt-1">Be honest. This number only has power if it reflects what you actually believe right now.</div>
              </div>

              <div className="mt-6 flex gap-3">
                <button onClick={() => setShowNew(false)} className="btn btn-ghost flex-1 py-2.5 rounded-xl">Cancel</button>
                <button onClick={createForecast} className="btn btn-primary flex-1 py-2.5 rounded-xl">Lay this layer down</button>
              </div>

              <div className="text-[10px] text-center text-[#8B7A5F] mt-4">This claim stays private to you unless you later choose to publish a wisdom tile derived from it.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* EXCAVATION MODAL — where the actual insight and calibration error appear */}
      <AnimatePresence>
        {excavating && (
          <div className="fixed inset-0 bg-black/80 z-[70] flex items-start justify-center pt-10 pb-10 px-4 overflow-auto" onClick={() => setExcavating(null)}>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onClick={e => e.stopPropagation()} className="card w-full max-w-2xl rounded-2xl overflow-hidden">
              <div className="excavation-header px-6 pt-5 pb-4 border-b border-[#342F2B]">
                <div className="flex justify-between">
                  <div className="text-xs uppercase tracking-widest text-[#8B7A5F]">excavation</div>
                  <button onClick={() => setExcavating(null)}><X /></button>
                </div>
                <div className="mt-1 text-xl leading-tight pr-6">{excavating.prediction}</div>
                <div className="mt-1 text-sm text-[#A68B6B]">{CATEGORY_LABELS[excavating.category]} • target {format(parseISO(excavating.target_date), 'MMMM d, yyyy')}</div>
              </div>

              {/* Strata timeline */}
              <div className="p-6 space-y-4">
                <div className="uppercase text-xs text-[#8B7A5F] tracking-widest mb-1">STRATA (YOUR PROBABILITY OVER TIME)</div>
                
                {/* Initial */}
                <div className="stratum pl-9">
                  <div className="absolute left-[13px] top-1.5 w-2 h-2 rounded-full bg-[#8B6F47]" />
                  <div className="text-xs text-[#8B7A5F]">{format(parseISO(excavating.created_at), 'MMM d, HH:mm')}</div>
                  <div className="font-medium">Initial: <span className="mono tabular-nums">{Math.round(excavating.initial_p * 100)}%</span></div>
                </div>

                {getForecastUpdates(excavating.id).map((u, idx) => (
                  <div key={u.id} className="stratum pl-9">
                    <div className="absolute left-[13px] top-1.5 w-2 h-2 rounded-full bg-[#A68B6B]" />
                    <div className="text-xs text-[#8B7A5F]">{format(parseISO(u.created_at), 'MMM d, HH:mm')}</div>
                    <div>Updated to <span className="mono font-medium tabular-nums">{Math.round(u.p * 100)}%</span></div>
                    {u.note && <div className="text-sm text-[#C9B8A8] mt-0.5">“{u.note}”</div>}
                  </div>
                ))}

                {excavating.resolved && (
                  <div className="stratum pl-9 pt-1">
                    <div className="absolute left-[13px] top-1.5 w-2 h-2 rounded-full bg-[#B36B5E]" />
                    <div className="text-xs text-[#B36B5E]">RESOLVED</div>
                    <div className="font-medium">Outcome: <span className={excavating.outcome ? 'text-[#7E9E6B]' : 'text-[#B36B5E]'}>{excavating.outcome ? 'TRUE' : 'FALSE'}</span> • absolute drift <span className="mono">{excavating.abs_error?.toFixed(2)}</span></div>
                    {excavating.outcome_note && <div className="text-sm mt-0.5 text-[#C9B8A8]">“{excavating.outcome_note}”</div>}
                  </div>
                )}
              </div>

              {/* Add update */}
              {!excavating.resolved && (
                <div className="border-t border-[#342F2B] px-6 py-5 bg-[#211D1A]">
                  <div className="uppercase text-xs text-[#8B7A5F] mb-2">ADD AN UPDATE (NEW EVIDENCE)</div>
                  <UpdateForm onAdd={(p, note) => addUpdate(excavating.id, p, note)} />
                </div>
              )}

              {/* Resolve block */}
              {!excavating.resolved && (
                <div className="border-t border-[#342F2B] px-6 py-5">
                  <div className="uppercase text-xs tracking-widest text-[#8B7A5F] mb-3">RESOLVE THIS FORECAST</div>
                  <ResolveForm 
                    onResolve={(outcome, note, rb, ra) => resolveForecast(excavating.id, outcome, note, rb, ra)} 
                    initialDate={excavating.target_date} 
                  />
                </div>
              )}

              {/* Post-resolve actions: tile + note */}
              {excavating.resolved && (
                <div className="border-t border-[#342F2B] p-6 bg-[#211D1A] flex flex-col gap-3">
                  <button onClick={() => openTileComposer(excavating)} className="btn btn-primary w-full py-2.5 rounded-xl flex items-center justify-center gap-2">
                    <Archive className="w-4 h-4" /> Mint a public wisdom tile from this excavation
                  </button>
                  <div className="text-[11px] text-center text-[#8B7A5F]">A tile is a short, honest observation you choose to contribute to the commons. It can never be traced back to you.</div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* TILE COMPOSER + PREVIEW (the beautiful exportable artifact) */}
      <AnimatePresence>
        {showTileComposer && (
          <div className="fixed inset-0 bg-black/80 z-[80] flex items-center justify-center p-4" onClick={() => setShowTileComposer(null)}>
            <div onClick={e => e.stopPropagation()} className="w-full max-w-lg">
              <div className="mb-3 flex justify-between items-center px-1">
                <div className="font-medium">Compose wisdom tile</div>
                <button onClick={() => setShowTileComposer(null)}><X /></button>
              </div>

              <textarea value={tileDraftText} onChange={e => setTileDraftText(e.target.value)} rows={5} className="input w-full p-4 rounded-2xl text-[15px] leading-snug" />

              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest mb-2 px-1 text-[#8B7A5F]">PREVIEW (this is exactly what will be published + exported)</div>
                <div id="tile-preview" className="max-w-[420px]">
                  <WisdomTileCard tile={{
                    id: 'preview',
                    created_at: new Date().toISOString(),
                    tile_text: tileDraftText || 'Your observation will appear here.',
                    stats: {
                      initial_p: showTileComposer.initial_p,
                      outcome: showTileComposer.outcome!,
                      abs_error: showTileComposer.abs_error!,
                      update_count: showTileComposer.update_count,
                      category: showTileComposer.category,
                      horizon_days: horizonDays(showTileComposer.target_date),
                    },
                    source_type: 'personal',
                  }} />
                </div>
              </div>

              <div className="mt-4 flex gap-3">
                <button onClick={() => setShowTileComposer(null)} className="btn btn-ghost flex-1 py-2 rounded-xl">Cancel</button>
                <button onClick={() => publishTile(showTileComposer)} className="btn btn-primary flex-1 py-2 rounded-xl">Publish to public commons</button>
                <button onClick={exportTilePNG} disabled={isExportingTile} className="btn btn-ghost flex items-center gap-2 px-5 rounded-xl"><Download className="w-4 h-4" /> PNG</button>
              </div>
              <div className="text-[10px] text-center mt-3 text-[#8B7A5F]">Publishing is irreversible for the commons, but your original forecast stays private.</div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* RECOVERY / IDENTITY / CLOUD / PRIVACY modal — full data sovereignty */}
      <AnimatePresence>
        {showRecovery && (
          <div className="fixed inset-0 bg-black/80 z-[90] flex items-center justify-center p-4" onClick={() => setShowRecovery(false)}>
            <div onClick={e => e.stopPropagation()} className="card w-full max-w-md rounded-2xl p-6 text-sm">
              <div className="font-semibold text-lg tracking-tight mb-1">Your dig key &amp; data controls</div>
              <div className="text-[#A68B6B] text-xs mb-4">This is the only identity that exists. Lose it and you lose access to cloud-synced strata across devices (unless you exported).</div>

              <div className="mono text-xs bg-[#211D1A] p-3 rounded-lg break-all border border-[#342F2B] mb-3">{userId}</div>
              <div className="flex gap-2 mb-5">
                <button onClick={copyRecoveryKey} className="btn btn-ghost flex-1 py-1.5 rounded-lg text-xs">Copy key</button>
                <button onClick={exportAllData} className="btn btn-ghost flex-1 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1"><Download className="w-3.5 h-3.5" /> Export JSON archive</button>
              </div>

              <div className="mb-2 text-xs uppercase tracking-widest text-[#8B7A5F]">Cloud worker (optional)</div>
              <input value={workerUrl} onChange={e => setWorkerUrl(e.target.value)} placeholder="https://drift-worker.your.workers.dev" className="input w-full p-2 rounded-lg text-sm mb-2 font-mono text-xs" />
              <div className="flex gap-2">
                <button onClick={syncWithWorker} disabled={syncing} className="btn btn-ghost flex-1 py-1.5 text-xs rounded-lg flex items-center justify-center gap-1.5"><RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> Sync now</button>
                <button onClick={() => { localStorage.removeItem(WORKER_URL_KEY); setWorkerUrl(''); toast('Worker URL cleared.') }} className="btn btn-ghost text-xs px-4 rounded-lg">Forget</button>
              </div>

              <div className="h-px bg-[#342F2B] my-5" />

              <button onClick={hardDeleteEverything} className="w-full flex items-center justify-center gap-2 text-[#B36B5E] hover:text-[#E8A89A] py-2 text-sm"><Trash2 className="w-4 h-4" /> Permanently delete everything in this browser</button>

              <div className="text-[10px] text-[#8B7A5F] mt-4 leading-snug">Deleting here only clears the browser. If you have synced to a worker, use the worker’s purge endpoint (or delete the D1 rows by your key) for full removal. Public wisdom tiles you published stay in the commons — they contain no identifying information by design.</div>

              <div className="mt-5 text-right">
                <button onClick={() => setShowRecovery(false)} className="text-xs underline">Close</button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Small controlled form components living inside the file for self-containment
function UpdateForm({ onAdd }: { onAdd: (p: number, note?: string) => void }) {
  const [p, setP] = useState(50)
  const [note, setNote] = useState('')
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <input type="range" min={5} max={95} value={p} onChange={e => setP(+e.target.value)} className="slider flex-1 accent-[#8B6F47]" />
        <div className="mono w-12 text-right font-medium">{p}%</div>
      </div>
      <input value={note} onChange={e => setNote(e.target.value)} placeholder="What changed? (optional, one sentence)" className="input w-full p-2 text-sm rounded-lg mb-2" />
      <button onClick={() => { onAdd(p, note); setNote(''); setP(50) }} className="btn btn-primary w-full py-2 rounded-lg text-sm">Log this update</button>
    </div>
  )
}

function ResolveForm({ onResolve, initialDate }: { onResolve: (outcome: 0 | 1, note: string, rb?: number, ra?: number) => void, initialDate: string }) {
  const [outcome, setOutcome] = useState<0 | 1 | null>(null)
  const [note, setNote] = useState('')
  const [rb, setRb] = useState<number | ''>('')
  const [ra, setRa] = useState<number | ''>('')
  const can = outcome !== null
  return (
    <div>
      <div className="flex gap-3 mb-4">
        <button onClick={() => setOutcome(1)} className={`flex-1 py-2 rounded-xl border text-sm ${outcome === 1 ? 'bg-[#2A3326] border-[#7E9E6B] text-[#7E9E6B]' : 'border-[#463F38] hover:bg-[#26221F]'}`}>It happened (true)</button>
        <button onClick={() => setOutcome(0)} className={`flex-1 py-2 rounded-xl border text-sm ${outcome === 0 ? 'bg-[#332826] border-[#B36B5E] text-[#B36B5E]' : 'border-[#463F38] hover:bg-[#26221F]'}`}>It did not happen (false)</button>
      </div>

      <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="What actually occurred? (optional — balance, date, source…)" rows={2} className="input w-full p-3 text-sm rounded-xl mb-3" />

      <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
        <div>
          <div className="text-[#8B7A5F] mb-1">Minutes ruminating BEFORE you recorded the forecast (optional)</div>
          <input type="number" value={rb} onChange={e => setRb(e.target.value === '' ? '' : +e.target.value)} className="input w-full p-2 rounded-lg" placeholder="e.g. 45" />
        </div>
        <div>
          <div className="text-[#8B7A5F] mb-1">Minutes it continued to occupy you AFTER recording (optional)</div>
          <input type="number" value={ra} onChange={e => setRa(e.target.value === '' ? '' : +e.target.value)} className="input w-full p-2 rounded-lg" placeholder="e.g. 8" />
        </div>
      </div>

      <button disabled={!can} onClick={() => onResolve(outcome!, note, rb === '' ? undefined : +rb, ra === '' ? undefined : +ra)} className="btn btn-primary disabled:opacity-40 w-full py-2.5 rounded-xl">Resolve + compute calibration error</button>
      <div className="text-[10px] text-center mt-2 text-[#8B7A5F]">This is the moment the forecast meets reality. The numbers are for you.</div>
    </div>
  )
}
